<template>
  <TreeSelect
    showSearch
    treeNodeFilterProp="title"
    v-bind="getAttrs"
    :field-names="fieldNames"
    :load-data="async ? onLoadData : undefined"
    @change="handleChange"
  >
    <template v-for="item in Object.keys($slots)" #[item]="data">
      <slot :name="item" v-bind="data || {}"></slot>
    </template>
    <template v-if="loading" #suffixIcon>
      <LoadingOutlined spin />
    </template>
  </TreeSelect>
</template>

<script lang="ts" setup>
  import { LoadingOutlined } from '@ant-design/icons-vue';
  import { TreeSelect } from 'ant-design-vue';
  import { get } from 'lodash-es';
  import { computed, onMounted, type PropType, ref, unref, useAttrs, watch } from 'vue';
  import type { Fn, Recordable } from '@mts-fe/types';
  import { isArray, isFunction, propTypes } from '@mts-fe/utils';
  import { t } from '@mts/hooks/web/useI18n';

  defineOptions({ name: 'ApiTreeSelect' });

  const props = defineProps({
    api: { type: Function as PropType<(arg?: any) => Promise<Recordable<any>>> },
    params: { type: Object },
    immediate: { type: Boolean, default: true },
    async: { type: Boolean, default: false },
    resultField: propTypes.string.def(''),
    labelField: propTypes.string.def('title'),
    valueField: propTypes.string.def('value'),
    childrenField: propTypes.string.def('children'),
    beforeFetch: {
      type: Function as PropType<Fn>,
      default: null,
    },
    afterFetch: {
      type: Function as PropType<Fn>,
      default: null,
    },
  });

  const emit = defineEmits(['options-change', 'change', 'load-data']);

  const attrs = useAttrs();
  const treeData = ref<Recordable<any>[]>([]);
  const isFirstLoaded = ref<boolean>(false);
  const loading = ref(false);
  const getAttrs = computed(() => {
    return {
      ...attrs,
      treeData: translateLabel(
        props.api ? unref(treeData) : ((attrs.treeData || []) as Recordable[]),
      ),
    };
  });
  const fieldNames = {
    children: props.childrenField,
    value: props.valueField,
    label: props.labelField,
  };

  function handleChange(...args) {
    emit('change', ...args);
  }

  watch(
    () => props.params,
    () => {
      !unref(isFirstLoaded) && fetch();
    },
    { deep: true },
  );

  watch(
    () => props.immediate,
    (v) => {
      v && !isFirstLoaded.value && fetch();
    },
  );

  onMounted(() => {
    props.immediate && fetch();
  });

  function onLoadData(treeNode) {
    return new Promise((resolve: (value?: unknown) => void) => {
      if (isArray(treeNode.children) && treeNode.children.length > 0) {
        resolve();
        return;
      }
      emit('load-data', { treeData, treeNode, resolve });
    });
  }

  async function fetch() {
    let { api, beforeFetch, afterFetch, params, resultField } = props;
    if (!api || !isFunction(api) || loading.value) return;
    loading.value = true;
    treeData.value = [];
    let res;
    try {
      if (beforeFetch && isFunction(beforeFetch)) {
        params = (await beforeFetch(params)) || params;
      }
      res = await api(params);
      if (afterFetch && isFunction(afterFetch)) {
        res = (await afterFetch(res)) || res;
      }
    } catch (e) {
      console.error(e);
    }
    loading.value = false;
    if (!res) return;
    if (resultField) {
      res = get(res, resultField) || [];
    }
    treeData.value = res;
    isFirstLoaded.value = true;
    emit('options-change', treeData.value);
  }

  // 多语言转换
  const translateLabel = (data: Recordable[]) => {
    if (!data) return [];
    data.forEach((item) => {
      item[props.labelField] = t(item[props.labelField]);

      if (item.children?.length) {
        item.children = translateLabel(item.children);
      }
    });
    return data;
  };
</script>
